import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavItems } from "./Sidebar";

/**
 * KOMUT PALETİ — Ctrl+K (Mac'te ⌘K) ya da üst çubuktaki düğme.
 *
 * Yazarken sayfalar süzülür; Enter seçili sayfaya gider. Yazılan metin iki
 * harften uzunsa ilk seçenek "Ara: …" olur ve genel aramayı açar (sipariş no,
 * müşteri, ürün). Yeni bağımlılık yok — düz React.
 */

type Item =
  | { kind: "page"; to: string; label: string; section: string }
  | { kind: "search"; q: string };

/** Türkçe büyük/küçük harf ve aksan farkına duyarsız karşılaştırma anahtarı. */
function key(s: string): string {
  return s
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function useCommandPaletteHotkey(onOpen: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onOpen]);
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavItems();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 10);
    return () => window.clearTimeout(t);
  }, [open]);

  const items: Item[] = useMemo(() => {
    const k = key(q.trim());
    const pages: Item[] = nav
      .filter((n) => !k || key(n.label).includes(k) || key(n.section).includes(k))
      .map((n) => ({ kind: "page", to: n.to, label: n.label, section: n.section }));
    if (q.trim().length >= 2) return [{ kind: "search", q: q.trim() }, ...pages];
    return pages;
  }, [nav, q]);

  useEffect(() => {
    if (active >= items.length) setActive(Math.max(0, items.length - 1));
  }, [items.length, active]);

  if (!open) return null;

  const go = (it: Item | undefined) => {
    if (!it) return;
    onClose();
    if (it.kind === "page") navigate(it.to);
    else navigate(`/search?q=${encodeURIComponent(it.q)}`);
  };

  return (
    <div className="noprint fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
      <div className="fixed inset-0 bg-gray-950/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Hızlı geçiş"
        className="relative w-full max-w-xl animate-fade-in overflow-hidden rounded-2xl bg-white shadow-pop ring-1 ring-gray-900/10"
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          else if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(items.length - 1, a + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter") {
            e.preventDefault();
            go(items[active]);
          }
        }}
      >
        <div className="flex items-center gap-3 border-b border-gray-200 px-4">
          <svg className="h-5 w-5 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Sayfa adı yazın ya da sipariş no, müşteri, ürün arayın…"
            aria-label="Sayfa ya da kayıt ara"
            className="h-14 w-full border-0 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
          <span className="kbd">Esc</span>
        </div>
        <ul role="listbox" className="max-h-[50vh] overflow-y-auto p-2">
          {items.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-gray-500">Eşleşen sayfa yok.</li>
          ) : (
            items.map((it, i) => (
              <li
                key={it.kind === "page" ? it.to : "__ara"}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(it)}
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm ${
                  i === active ? "bg-primary-50 text-primary-900" : "text-gray-700"
                }`}
              >
                {it.kind === "page" ? (
                  <>
                    <span className="font-medium">{it.label}</span>
                    <span className="text-xs text-gray-400">{it.section}</span>
                  </>
                ) : (
                  <>
                    <span>
                      Ara: <span className="font-semibold">&ldquo;{it.q}&rdquo;</span>
                    </span>
                    <span className="text-xs text-gray-400">sipariş · müşteri · ürün</span>
                  </>
                )}
              </li>
            ))
          )}
        </ul>
        <div className="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2 text-[11px] text-gray-500">
          <span>
            <span className="kbd">↑</span> <span className="kbd">↓</span> gez
          </span>
          <span>
            <span className="kbd">Enter</span> aç
          </span>
          <span className="ml-auto">
            <span className="kbd">Ctrl</span> <span className="kbd">K</span> her yerden
          </span>
        </div>
      </div>
    </div>
  );
}
